import { createAutoVideoKey, normalizeVideoSourceUrl } from '@vlt/shared';
import type {
  CommonPayload,
  EventName,
  Logger,
  Transport,
  VideoCtaAttribution,
  VideoState,
  VideoSummary,
} from './types';

export const VIDEO_SELECTOR =
  'video, iframe[src*="youtube.com/embed/"], iframe[src*="youtube-nocookie.com/embed/"]';
const CTA_ID_MAX_LENGTH = 128;
const CTA_NAME_MAX_LENGTH = 255;
const URL_MAX_LENGTH = 2048;
const TITLE_MAX_LENGTH = 255;
const YOUTUBE_IFRAME_SELECTOR =
  'iframe[src*="youtube.com/embed/"], iframe[src*="youtube-nocookie.com/embed/"]';
const YOUTUBE_ORIGINS = new Set(['https://www.youtube.com', 'https://www.youtube-nocookie.com']);
const YOUTUBE_IFRAME_API_SRC = 'https://www.youtube.com/iframe_api';
const YOUTUBE_PLAYING = 1;
const YOUTUBE_ENDED = 0;
const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
const CTA_CONTAINER_SELECTOR =
  '[data-vlt-video-container], [data-vlt-section], section, article, header, aside';

export { createAutoVideoKey, normalizeVideoSourceUrl } from '@vlt/shared';

type VideoTrackerOptions = {
  getCommonPayload: () => CommonPayload;
  transport: Transport;
  logger: Logger;
  datalayer: {
    pushDataLayer: (payload: Record<string, unknown>) => void;
  };
};

export type VideoTracker = ReturnType<typeof createVideoTracker>;
type TrackableVideoElement = HTMLVideoElement | HTMLIFrameElement;
type YouTubePlayer = {
  getCurrentTime?: () => number;
  getDuration?: () => number;
};
type YouTubePlayerEvent = {
  data: number;
  target: YouTubePlayer;
};
type YouTubePlayerConstructor = new (
  element: HTMLIFrameElement,
  options: {
    events: {
      onStateChange: (event: YouTubePlayerEvent) => void;
    };
  },
) => YouTubePlayer;
type YouTubeNamespace = {
  Player?: YouTubePlayerConstructor;
  PlayerState?: {
    PLAYING?: number;
    ENDED?: number;
  };
};

declare global {
  interface Window {
    YT?: YouTubeNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let youtubeApiReadyPromise: Promise<void> | null = null;

function getVideoUrl(video: TrackableVideoElement): string | null {
  if (video instanceof HTMLIFrameElement) {
    return video.src || video.getAttribute('src') || null;
  }
  const source = video.querySelector<HTMLSourceElement>('source[src]');
  return video.currentSrc || source?.src || video.getAttribute('src') || null;
}

function normalizeAbsoluteUrl(rawUrl: string | null): string | null {
  if (!rawUrl) return null;

  try {
    const url = new URL(rawUrl, window.location.href).href;
    return url.length <= URL_MAX_LENGTH ? url : null;
  } catch {
    return null;
  }
}

function normalizeText(value: string | null | undefined, maxLength: number): string | null {
  const normalized = value?.trim();
  if (!normalized) return null;
  return truncate(normalized, maxLength);
}

function getDetectedTitle(video: TrackableVideoElement): string | null {
  return normalizeText(
    video.getAttribute('title') || video.dataset.vltVideoTitle,
    TITLE_MAX_LENGTH,
  );
}

function getPageHeading(video: TrackableVideoElement): string | null {
  const section = video.closest('section, article, main, header, aside');
  const heading = section?.querySelector<HTMLElement>('h1, h2, h3, [data-vlt-heading]');
  return normalizeText(heading?.textContent, TITLE_MAX_LENGTH);
}

function getYouTubeEmbedId(sourceUrl: string): string | null {
  try {
    const url = new URL(sourceUrl, window.location.href);
    if (!YOUTUBE_ORIGINS.has(url.origin)) return null;

    const pathParts = url.pathname.split('/').filter(Boolean);
    if (pathParts.length !== 2 || pathParts[0] !== 'embed') return null;

    const youtubeId = pathParts[1];
    if (!youtubeId || !YOUTUBE_ID_PATTERN.test(youtubeId)) return null;
    return youtubeId;
  } catch {
    return null;
  }
}

function getYouTubeThumbnailUrl(youtubeId: string): string {
  return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
}

function createLegacyState(video: TrackableVideoElement): VideoState {
  return {
    videoId: video.dataset.vltVideoId ?? '',
    videoTitle:
      video.dataset.vltVideoTitle ||
      video.getAttribute('title') ||
      video.dataset.vltVideoId ||
      'Untitled video',
    videoUrl: getVideoUrl(video),
    autoDetected: false,
    sourceUrl: null,
    posterUrl: null,
    detectedTitle: null,
    pageHeading: null,
    started: false,
    completed: false,
    maxPercent: 0,
    lastPosition: 0,
    duration:
      video instanceof HTMLVideoElement && Number.isFinite(video.duration) ? video.duration : 0,
    progress25Sent: false,
    progress50Sent: false,
    progress75Sent: false,
    completeSent: false,
    ctaClicked: false,
    ctaId: null,
    ctaName: null,
    ctaAttribution: 'unknown',
    lastPlayedAt: 0,
  };
}

function createAutoState(
  video: TrackableVideoElement,
  videoId: string,
  sourceUrl: string,
): VideoState {
  const detectedTitle = getDetectedTitle(video);
  const youtubeId = video instanceof HTMLIFrameElement ? getYouTubeEmbedId(sourceUrl) : null;
  return {
    videoId,
    videoTitle: detectedTitle || youtubeId || videoId,
    videoUrl: getVideoUrl(video),
    autoDetected: true,
    sourceUrl,
    posterUrl:
      video instanceof HTMLVideoElement
        ? normalizeAbsoluteUrl(video.getAttribute('poster'))
        : youtubeId
          ? getYouTubeThumbnailUrl(youtubeId)
          : null,
    detectedTitle,
    pageHeading: getPageHeading(video),
    started: false,
    completed: false,
    maxPercent: 0,
    lastPosition: 0,
    duration:
      video instanceof HTMLVideoElement && Number.isFinite(video.duration) ? video.duration : 0,
    progress25Sent: false,
    progress50Sent: false,
    progress75Sent: false,
    completeSent: false,
    ctaClicked: false,
    ctaId: null,
    ctaName: null,
    ctaAttribution: 'unknown',
    lastPlayedAt: 0,
  };
}

function calcPercent(currentTime: number, duration: number): number | null {
  if (!Number.isFinite(duration) || duration <= 0) return null;
  return Math.max(0, Math.min(100, Math.floor((currentTime / duration) * 100)));
}

function updateVideoProgress(
  state: VideoState,
  currentTime: number,
  duration: number,
  videoUrl: string | null,
): void {
  const percent = calcPercent(currentTime, duration);
  if (percent == null) return;
  state.lastPosition = currentTime;
  state.duration = duration;
  state.videoUrl = videoUrl;
  if (percent > state.maxPercent) state.maxPercent = percent;
}

function updateMaxPercent(state: VideoState, video: HTMLVideoElement): void {
  updateVideoProgress(state, video.currentTime, video.duration, getVideoUrl(video));
}

function toSummary(state: VideoState): VideoSummary {
  return {
    video_id: state.videoId,
    video_title: state.videoTitle,
    video_url: state.videoUrl,
    video_started: state.started,
    video_max_percent: state.maxPercent,
    video_completed: state.completed,
    video_last_position: state.lastPosition,
    video_duration: state.duration,
    video_cta_clicked: state.ctaClicked,
    video_cta_id: state.ctaId,
    video_cta_name: state.ctaName,
    video_cta_attribution: state.ctaAttribution,
  };
}

function dataLayerPayload(
  event: EventName,
  state: VideoState,
  videoPercent?: number,
): Record<string, unknown> {
  return {
    event,
    video_id: state.videoId,
    video_title: state.videoTitle,
    video_url: state.videoUrl,
    video_percent: videoPercent,
    video_max_percent: state.maxPercent,
    video_last_position: state.lastPosition,
    video_duration: state.duration,
    video_completed: state.completed,
    video_cta_clicked: state.ctaClicked,
    video_cta_id: state.ctaId,
    video_cta_name: state.ctaName,
    video_cta_attribution: state.ctaAttribution,
  };
}

function eventVideoPayload(
  state: VideoState,
  videoPercent?: number,
): NonNullable<Parameters<Transport['sendEvent']>[0]['video']> {
  const payload: NonNullable<Parameters<Transport['sendEvent']>[0]['video']> = {
    video_id: state.videoId,
    video_title: state.videoTitle,
    video_url: state.videoUrl,
    video_percent: videoPercent,
    video_max_percent: state.maxPercent,
    video_last_position: state.lastPosition,
    video_duration: state.duration,
    video_completed: state.completed,
    video_cta_clicked: state.ctaClicked,
    video_cta_id: state.ctaId,
    video_cta_name: state.ctaName,
    video_cta_attribution: state.ctaAttribution,
  };

  if (state.autoDetected) {
    payload.source_url = state.sourceUrl;
    payload.poster_url = state.posterUrl;
    payload.detected_title = state.detectedTitle;
    payload.page_heading = state.pageHeading;
  }

  return payload;
}

function comparePrimary(a: VideoState, b: VideoState): number {
  if (Number(a.ctaClicked) !== Number(b.ctaClicked))
    return Number(b.ctaClicked) - Number(a.ctaClicked);
  if (Number(a.completed) !== Number(b.completed)) return Number(b.completed) - Number(a.completed);
  if (a.maxPercent !== b.maxPercent) return b.maxPercent - a.maxPercent;
  if (a.lastPosition !== b.lastPosition) return b.lastPosition - a.lastPosition;
  return b.lastPlayedAt - a.lastPlayedAt;
}

function truncate(value: string, maxLength: number): string {
  return value.length > maxLength ? value.slice(0, maxLength) : value;
}

function normalizeComparableUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  return normalizeVideoSourceUrl(value) ?? normalizeAbsoluteUrl(value);
}

function explicitBindingMatches(
  binding: string,
  video: TrackableVideoElement,
  state: VideoState,
): boolean {
  if (state.videoId === binding) return true;
  if (video.dataset.vltVideoId?.trim() === binding) return true;

  const normalizedBinding = normalizeComparableUrl(binding);
  if (!normalizedBinding) return false;

  return [state.sourceUrl, state.videoUrl, getVideoUrl(video)]
    .map(normalizeComparableUrl)
    .some((candidate) => candidate === normalizedBinding);
}

function sameContainerMatches(target: HTMLElement, video: TrackableVideoElement): boolean {
  const targetContainers = target.closest(CTA_CONTAINER_SELECTOR)
    ? [target.closest(CTA_CONTAINER_SELECTOR)]
    : [];
  for (const container of targetContainers) {
    if (!container) continue;
    if (container.contains(video)) return true;
    if (video.closest(CTA_CONTAINER_SELECTOR) === container) return true;
  }
  return false;
}

function isYouTubeApiReady(): boolean {
  return typeof window.YT?.Player === 'function';
}

function loadYouTubeIframeApi(): Promise<void> {
  if (isYouTubeApiReady()) return Promise.resolve();
  if (youtubeApiReadyPromise) return youtubeApiReadyPromise;

  youtubeApiReadyPromise = new Promise((resolve) => {
    const previousReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousReady?.();
      resolve();
    };

    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${YOUTUBE_IFRAME_API_SRC}"]`,
    );
    if (existingScript) return;

    const script = document.createElement('script');
    script.src = YOUTUBE_IFRAME_API_SRC;
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
    if (!firstScript?.parentNode) document.head.appendChild(script);
  });

  return youtubeApiReadyPromise;
}

export function createVideoTracker({
  getCommonPayload,
  transport,
  logger,
  datalayer,
}: VideoTrackerOptions) {
  const states = new Map<TrackableVideoElement, VideoState>();
  const attaching = new WeakSet<TrackableVideoElement>();
  const youtubePollers = new WeakMap<YouTubePlayer, number>();

  function sendVideoEvent(eventName: EventName, state: VideoState, videoPercent?: number): void {
    const common = getCommonPayload();
    const payload = {
      ...common,
      event_name: eventName,
      video: eventVideoPayload(state, videoPercent),
      occurred_at: new Date().toISOString(),
    };

    logger.debug('video.event', payload);
    transport.sendEvent(payload);
    datalayer.pushDataLayer({
      ...dataLayerPayload(eventName, state, videoPercent),
      vlt_visitor_id: common.visitor_id,
      vlt_session_id: common.session_id,
      vlt_cid: common.cid,
      ...common.attribution,
      page_location: common.page_location,
      referrer: common.referrer,
    });
  }

  function maybeFireProgress(state: VideoState, threshold: 25 | 50 | 75): void {
    const flag = `progress${threshold}Sent` as
      | 'progress25Sent'
      | 'progress50Sent'
      | 'progress75Sent';
    if (state.maxPercent >= threshold && !state[flag]) {
      state[flag] = true;
      sendVideoEvent(`video_progress_${threshold}` as EventName, state, threshold);
    }
  }

  function fireStart(state: VideoState, videoPercent?: number): void {
    if (state.started) return;
    state.started = true;
    sendVideoEvent('video_start', state, videoPercent ?? state.maxPercent);
  }

  function fireComplete(state: VideoState): void {
    state.completed = true;
    state.maxPercent = Math.max(state.maxPercent, 100);
    if (state.completeSent) return;
    state.completeSent = true;
    sendVideoEvent('video_complete', state, 100);
  }

  function bindVideoEvents(video: HTMLVideoElement, state: VideoState): void {
    states.set(video, state);
    video.addEventListener('play', () => {
      state.lastPlayedAt = Date.now();
      state.videoUrl = getVideoUrl(video);
      updateMaxPercent(state, video);
      fireStart(state, state.maxPercent);
    });
    video.addEventListener('timeupdate', () => {
      state.lastPlayedAt = Date.now();
      updateMaxPercent(state, video);
      maybeFireProgress(state, 25);
      maybeFireProgress(state, 50);
      maybeFireProgress(state, 75);
    });
    video.addEventListener('ended', () => {
      state.lastPlayedAt = Date.now();
      updateMaxPercent(state, video);
      fireComplete(state);
    });
  }

  function isYouTubeIframe(element: TrackableVideoElement): element is HTMLIFrameElement {
    return element instanceof HTMLIFrameElement && element.matches(YOUTUBE_IFRAME_SELECTOR);
  }

  function ensureYouTubeJsApi(iframe: HTMLIFrameElement): string | null {
    const rawSrc = iframe.getAttribute('src');
    if (!rawSrc) return null;

    try {
      const url = new URL(rawSrc, window.location.href);
      if (!YOUTUBE_ORIGINS.has(url.origin)) return null;

      let changed = false;
      if (url.searchParams.get('enablejsapi') !== '1') {
        url.searchParams.set('enablejsapi', '1');
        changed = true;
      }
      if (!url.searchParams.has('origin') && window.location.origin) {
        url.searchParams.set('origin', window.location.origin);
        changed = true;
      }
      if (changed) iframe.src = url.href;
      return url.href;
    } catch {
      return null;
    }
  }

  function readYouTubeProgress(
    iframe: HTMLIFrameElement,
    player: YouTubePlayer,
    state: VideoState,
  ): void {
    const currentTime = player.getCurrentTime?.();
    const duration = player.getDuration?.();
    if (
      typeof currentTime !== 'number' ||
      !Number.isFinite(currentTime) ||
      typeof duration !== 'number' ||
      !Number.isFinite(duration)
    ) {
      return;
    }

    updateVideoProgress(state, currentTime, duration, getVideoUrl(iframe));
  }

  function updateYouTubeProgressThresholds(
    iframe: HTMLIFrameElement,
    player: YouTubePlayer,
    state: VideoState,
  ): void {
    readYouTubeProgress(iframe, player, state);
    maybeFireProgress(state, 25);
    maybeFireProgress(state, 50);
    maybeFireProgress(state, 75);
  }

  function startYouTubePolling(
    iframe: HTMLIFrameElement,
    player: YouTubePlayer,
    state: VideoState,
  ): void {
    if (youtubePollers.has(player)) return;
    const poller = window.setInterval(() => {
      updateYouTubeProgressThresholds(iframe, player, state);
    }, 1000);
    youtubePollers.set(player, poller);
  }

  function stopYouTubePolling(player: YouTubePlayer): void {
    const poller = youtubePollers.get(player);
    if (poller == null) return;
    window.clearInterval(poller);
    youtubePollers.delete(player);
  }

  async function bindYouTubeEvents(iframe: HTMLIFrameElement, state: VideoState): Promise<void> {
    states.set(iframe, state);
    await loadYouTubeIframeApi();
    const Player = window.YT?.Player;
    if (!Player) {
      logger.debug('video.auto_detect_skipped', { reason: 'youtube_api_unavailable' });
      return;
    }

    new Player(iframe, {
      events: {
        onStateChange: (event) => {
          const playingState = window.YT?.PlayerState?.PLAYING ?? YOUTUBE_PLAYING;
          const endedState = window.YT?.PlayerState?.ENDED ?? YOUTUBE_ENDED;

          if (event.data === playingState) {
            state.lastPlayedAt = Date.now();
            readYouTubeProgress(iframe, event.target, state);
            fireStart(state, state.maxPercent);
            startYouTubePolling(iframe, event.target, state);
          }

          if (event.data === endedState) {
            state.lastPlayedAt = Date.now();
            updateYouTubeProgressThresholds(iframe, event.target, state);
            stopYouTubePolling(event.target);
            fireComplete(state);
          }
        },
      },
    });
  }

  function attach(video: TrackableVideoElement): void | Promise<void> {
    if (states.has(video) || attaching.has(video)) return;
    if (isYouTubeIframe(video)) {
      const source = ensureYouTubeJsApi(video);
      const sourceUrl = normalizeVideoSourceUrl(source);
      if (!sourceUrl) {
        logger.debug('video.auto_detect_skipped', { reason: 'source_url_missing' });
        return;
      }
      attaching.add(video);
      return createAutoVideoKey(sourceUrl)
        .then(async (videoId) => {
          if (states.has(video)) return;
          await bindYouTubeEvents(video, createAutoState(video, videoId, sourceUrl));
        })
        .catch((error) => {
          logger.debug('video.auto_detect_skipped', {
            reason: error instanceof Error ? error.message : 'sha256_failed',
            sourceUrl,
          });
        })
        .finally(() => attaching.delete(video));
    }

    if (video.hasAttribute('data-vlt-video-id')) {
      const state = createLegacyState(video);
      if (!state.videoId) return;
      bindVideoEvents(video, state);
      return;
    }

    const sourceUrl = normalizeVideoSourceUrl(getVideoUrl(video));
    if (!sourceUrl) {
      logger.debug('video.auto_detect_skipped', { reason: 'source_url_missing' });
      return;
    }
    if (sourceUrl.length > URL_MAX_LENGTH) {
      logger.debug('video.auto_detect_skipped', { reason: 'source_url_too_long', sourceUrl });
      return;
    }

    return createAutoVideoKey(sourceUrl)
      .then((videoId) => {
        if (states.has(video)) return;
        bindVideoEvents(video, createAutoState(video, videoId, sourceUrl));
      })
      .catch((error) => {
        logger.debug('video.auto_detect_skipped', {
          reason: error instanceof Error ? error.message : 'sha256_failed',
          sourceUrl,
        });
      });
  }

  function pickPrimaryVideoSummary(): VideoSummary | null {
    const ranked = [...states.values()].sort(comparePrimary);
    return ranked[0] ? toSummary(ranked[0]) : null;
  }

  function resolveCtaVideoState(
    target: HTMLElement,
  ): { state: VideoState; attribution: VideoCtaAttribution } | null {
    const entries = [...states.entries()];
    const explicitVideoId = target.dataset.vltCtaVideoId?.trim();

    if (explicitVideoId) {
      const explicitMatches = entries
        .filter(([video, state]) => explicitBindingMatches(explicitVideoId, video, state))
        .map(([, state]) => state)
        .sort(comparePrimary);
      if (explicitMatches[0]) {
        return { state: explicitMatches[0], attribution: 'explicit' };
      }
    }

    const sameContainerMatchesList = entries
      .filter(([video]) => sameContainerMatches(target, video))
      .map(([, state]) => state)
      .sort(comparePrimary);
    if (sameContainerMatchesList[0]) {
      return { state: sameContainerMatchesList[0], attribution: 'same_section' };
    }

    const inferred = entries.map(([, state]) => state).sort(comparePrimary)[0];
    return inferred ? { state: inferred, attribution: 'inferred' } : null;
  }

  function markCtaClicked(target: HTMLElement, ctaId: string, ctaName: string | null): void {
    const resolved = resolveCtaVideoState(target);
    if (!resolved) {
      logger.debug('cta.click_ignored_no_video_state', { ctaId, ctaName });
      return;
    }

    const { state, attribution } = resolved;
    state.ctaClicked = true;
    state.ctaId = truncate(ctaId, CTA_ID_MAX_LENGTH);
    state.ctaAttribution = attribution;
    const resolvedCtaName = ctaName?.trim() || target.textContent?.trim() || null;
    state.ctaName = resolvedCtaName ? truncate(resolvedCtaName, CTA_NAME_MAX_LENGTH) : null;
    state.lastPlayedAt = Date.now();
    sendVideoEvent('video_cta_click', state, state.maxPercent);
  }

  function observe(): MutationObserver | null {
    if (!('MutationObserver' in window)) return null;
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.target instanceof HTMLIFrameElement &&
          mutation.target.matches(YOUTUBE_IFRAME_SELECTOR)
        ) {
          attach(mutation.target);
        }
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          if (node.matches(VIDEO_SELECTOR)) attach(node as TrackableVideoElement);
          node.querySelectorAll<TrackableVideoElement>(VIDEO_SELECTOR).forEach(attach);
        }
      }
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['src'],
      childList: true,
      subtree: true,
    });
    return observer;
  }

  return {
    attach,
    observe,
    pickPrimaryVideoSummary,
    markCtaClicked,
    states: () => [...states.values()],
  };
}
